import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_marketing-economy/artifacts/q6npai4a_907F1F75-F4C3-4C66-925B-49CBEC490807.PNG";

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const navLinks = [
    { label: t.nav.solution, href: '#solution' },
    { label: t.nav.howItWorks, href: '#how-it-works' },
    { label: t.nav.forBrands, href: '#for-brands' },
    { label: t.nav.pricing, href: '#pricing' },
    { label: t.nav.faq, href: '#faq' },
  ];
  
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };
  
  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass py-3 shadow-sm' : 'py-5 bg-transparent'
        }`}
      >
        <div className="section-container">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src={LOGO_URL} alt="AQERA" className="h-10 w-auto" />
            </a>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="text-[#475569] hover:text-[#2563EB] transition-colors text-sm font-medium"
                  data-testid={`nav-${link.href.replace('#', '')}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>
            
            {/* Right Side */}
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 text-[#475569] hover:text-[#2563EB] transition-colors text-sm"
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase font-medium">{language}</span>
              </button>
              
              {/* CTA Button - Desktop */}
              <a
                href="https://dashboard.aqera.app"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:inline-flex btn-primary text-sm py-3 px-6"
                data-testid="header-cta"
              >
                {t.hero.cta}
              </a>
              
              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-[#0F172A]"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>
      
      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-white/95 backdrop-blur-lg" onClick={() => setIsMobileMenuOpen(false)} />
            <div className="relative pt-24 px-6">
              <nav className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => scrollToSection(link.href)}
                    className="text-[#0F172A] text-lg font-medium py-3 border-b border-[#E2E8F0] text-left"
                    data-testid={`mobile-nav-${link.href.replace('#', '')}`}
                  >
                    {link.label}
                  </button>
                ))}
                <a
                  href="https://dashboard.aqera.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-center mt-4"
                  data-testid="mobile-cta"
                >
                  {t.hero.cta}
                </a>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
